import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Table, Button, Tag, Drawer, Avatar, Spin, Empty,
  Row, Col, Statistic, Timeline, Badge, Divider, Input,
  Select, Tooltip, message, Progress, Space
} from "antd";
import {
  StarOutlined, ExclamationCircleOutlined, PlusOutlined,
  UserOutlined, TeamOutlined, SearchOutlined, FilterOutlined,
  CalendarOutlined, MessageOutlined, FireOutlined, RiseOutlined,
  EyeOutlined, CommentOutlined, SendOutlined, CheckCircleOutlined,
  FileTextOutlined
} from "@ant-design/icons";

import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";
import TalentFeedbackModal from "../../../components/TalentFeedbackModal/TalentFeedbackModal";

const { TextArea } = Input;

const { Option } = Select;

const Wrapper = styled.div`
  padding: 0;

  .stat-card {
    border-radius: 12px;
    border: none;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }

  .perf-table .ant-table-thead > tr > th {
    background: #f8fafc;
    font-weight: 600;
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 2px solid #e8edf3;
  }

  .perf-table .ant-table-tbody > tr > td {
    vertical-align: middle;
    padding: 14px 16px;
  }

  .perf-table .ant-table-tbody > tr:hover > td {
    background: #f5f9ff !important;
  }

  .action-group .ant-btn {
    height: 30px;
    font-size: 12px;
    font-weight: 500;
  }

  .action-group .ant-btn:first-child {
    color: #014c75;
    border-color: #b3cfe0;
    background: #fff;
  }

  .action-group .ant-btn:first-child:hover {
    background: #f0f7ff;
    border-color: #014c75;
  }

  .action-group .ant-btn:last-child {
    background: #014c75;
    border-color: #014c75;
    color: #fff;
  }

  .action-group .ant-btn:last-child:hover {
    background: #016094;
    border-color: #016094;
  }
`;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const scoreColor = (score) => {
  if (score === null || score === undefined) return "#aaa";
  if (score >= 80) return "#52c41a";
  if (score >= 50) return "#1677ff";
  if (score >= 30) return "#fa8c16";
  return "#ff4d4f";
};

const CONTRACT_STATUS = {
  active: { label: "Active", color: "success" },
  inactive: { label: "Inactive", color: "default" },
  terminated: { label: "Terminated", color: "error" },
};

const feedbackTypeColor = { instant: "orange", monthly: "#1677ff", contract_end: "#7c3aed" };
const feedbackTypeLabel = (f) => {
  if (f.feedbackType === "instant") return "Instant";
  if (f.feedbackType === "monthly") return `Monthly – ${MONTHS[(f.month||1)-1]} ${f.year}`;
  return "Contract End";
};

const statusTag = (s) => {
  if (s === "open") return <Tag color="error" style={{ borderRadius: 20 }}>Open</Tag>;
  if (s === "acknowledged_by_partner") return <Tag color="warning" style={{ borderRadius: 20 }}>Acknowledged</Tag>;
  return <Tag color="default" style={{ borderRadius: 20 }}>Closed</Tag>;
};

const CustomerTalentPerformance = () => {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [replyOpen, setReplyOpen] = useState({});
  const [replyText, setReplyText] = useState({});
  const [submittingReply, setSubmittingReply] = useState({});
  const [resolvingId, setResolvingId] = useState(null);

  const token = () => localStorage.getItem("authToken");

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_CONST.PERFORMANCE_FEEDBACK_TALENT_SUMMARY, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setOverview(res.data.talents || []);
    } catch {
      message.error("Failed to load performance overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const fetchFeedbackForRecord = async (hiringRecordId) => {
    setFeedbackLoading(true);
    try {
      const res = await axios.get(
        `${API_CONST.PERFORMANCE_FEEDBACK_HIRING_RECORD}/${hiringRecordId}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setFeedbackHistory(res.data.feedback || []);
    } catch {
      setFeedbackHistory([]);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const openDrawer = (record) => {
    setSelected(record);
    setDrawerOpen(true);
    fetchFeedbackForRecord(record.hiringRecordId);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
    setFeedbackHistory([]);
    setReplyOpen({});
    setReplyText({});
    setSubmittingReply({});
  };

  const handleReply = async (feedbackId) => {
    const text = (replyText[feedbackId] || "").trim();
    if (!text) return;
    setSubmittingReply(p => ({ ...p, [feedbackId]: true }));
    try {
      await axios.post(
        `${API_CONST.PERFORMANCE_FEEDBACK}/${feedbackId}/response`,
        { content: text, responseType: "customer_reply" },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      message.success("Reply sent");
      setReplyText(p => ({ ...p, [feedbackId]: "" }));
      setReplyOpen(p => ({ ...p, [feedbackId]: false }));
      if (selected?.hiringRecordId) fetchFeedbackForRecord(selected.hiringRecordId);
    } catch {
      message.error("Failed to send reply");
    } finally {
      setSubmittingReply(p => ({ ...p, [feedbackId]: false }));
    }
  };

  const handleResolve = async (feedbackId) => {
    setResolvingId(feedbackId);
    try {
      await axios.patch(
        `${API_CONST.PERFORMANCE_FEEDBACK_RESOLVE}/${feedbackId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      message.success("Feedback marked as resolved");
      if (selected?.hiringRecordId) fetchFeedbackForRecord(selected.hiringRecordId);
    } catch {
      message.error("Failed to resolve feedback");
    } finally {
      setResolvingId(null);
    }
  };

  const filtered = overview.filter(r => {
    const name = r.talent?.name || "";
    const role = r.talent?.role || "";
    const matchSearch = !search
      || name.toLowerCase().includes(search.toLowerCase())
      || role.toLowerCase().includes(search.toLowerCase())
      || (r.jobId || "").toLowerCase().includes(search.toLowerCase())
      || (r.hiringRecordId || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus
      || (filterStatus === "with_issues" ? r.openIssues > 0
        : filterStatus === "no_feedback" ? r.totalFeedback === 0
        : r.contractStatus === filterStatus);
    return matchSearch && matchStatus;
  });

  const totalHired = overview.length;
  const totalOpenIssues = overview.reduce((s, r) => s + r.openIssues, 0);
  const scored = overview.filter(r => r.avgScore !== null);
  const overallAvg = scored.length > 0
    ? Math.round(scored.reduce((s, r) => s + r.avgScore, 0) / scored.length)
    : null;
  const recentActivity = overview.filter(r =>
    r.lastFeedbackDate && new Date(r.lastFeedbackDate) > new Date(Date.now() - 30 * 86400000)
  ).length;

  const columns = [
    {
      title: "Talent",
      key: "talent",
      width: 200,
      render: (_, r) => (
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
          onClick={() => openDrawer(r)}
        >
          <Avatar
            size={38}
            icon={<UserOutlined />}
            style={{
              background: "linear-gradient(135deg, #014c75, #0288c7)",
              flexShrink: 0,
              fontSize: 16,
            }}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#014c75", fontSize: 13, lineHeight: 1.3 }}>
              {r.talent?.name || "—"}
            </div>
            <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>{r.talent?.role || "—"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 130,
      render: v => v ? (
        <Tooltip title={v}>
          <Tag icon={<FileTextOutlined />} color="blue" style={{ borderRadius: 6, fontSize: 11, cursor: "default", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis" }}>
            {v}
          </Tag>
        </Tooltip>
      ) : <span style={{ color: "#ddd", fontSize: 12 }}>—</span>,
    },
    {
      title: "Contract",
      dataIndex: "hiringRecordId",
      key: "contractId",
      width: 130,
      render: v => v ? (
        <Tooltip title={v}>
          <Tag color="geekblue" style={{ borderRadius: 6, fontSize: 11, cursor: "default", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis" }}>
            {v}
          </Tag>
        </Tooltip>
      ) : <span style={{ color: "#ddd", fontSize: 12 }}>—</span>,
    },
    {
      title: "Hired Since",
      dataIndex: "onboardingDate",
      key: "onboarding",
      width: 110,
      render: v => v
        ? <span style={{ fontSize: 12, color: "#666" }}>{new Date(v).toLocaleDateString("en-IN")}</span>
        : <span style={{ color: "#ddd" }}>—</span>,
    },
    {
      title: "Status",
      dataIndex: "contractStatus",
      key: "status",
      width: 100,
      render: v => {
        const s = CONTRACT_STATUS[v] || { label: v || "—", color: "default" };
        return <Tag color={s.color} style={{ borderRadius: 20, fontSize: 11 }}>{s.label}</Tag>;
      },
    },
    {
      title: "Feedback",
      key: "stats",
      width: 120,
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "2px 10px",
              borderRadius: 20,
              background: r.totalFeedback > 0 ? "#e6f4ff" : "#f5f5f5",
              border: `1px solid ${r.totalFeedback > 0 ? "#91caff" : "#e0e0e0"}`,
            }}
          >
            <CommentOutlined style={{ fontSize: 10, color: r.totalFeedback > 0 ? "#1677ff" : "#bbb" }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: r.totalFeedback > 0 ? "#1677ff" : "#bbb" }}>
              {r.totalFeedback}
            </span>
          </div>
          {r.openIssues > 0 && (
            <Tooltip title={`${r.openIssues} open issue${r.openIssues > 1 ? "s" : ""}`}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 8px",
                  borderRadius: 20,
                  background: "#fff2f0",
                  border: "1px solid #ffa39e",
                }}
              >
                <ExclamationCircleOutlined style={{ fontSize: 10, color: "#ff4d4f" }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: "#ff4d4f" }}>{r.openIssues}</span>
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Avg Score",
      key: "avgScore",
      width: 90,
      render: (_, r) => r.avgScore !== null ? (
        <Tooltip title={`${r.avgScore}% performance score`}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Progress
              type="circle"
              percent={r.avgScore}
              size={38}
              strokeWidth={8}
              strokeColor={scoreColor(r.avgScore)}
              trailColor="#f0f0f0"
              format={p => (
                <span style={{ fontSize: 9, fontWeight: 700, color: scoreColor(r.avgScore) }}>
                  {p}%
                </span>
              )}
            />
          </div>
        </Tooltip>
      ) : (
        <div style={{ textAlign: "center" }}>
          <span
            style={{
              fontSize: 11,
              color: "#bbb",
              padding: "2px 8px",
              background: "#fafafa",
              borderRadius: 20,
              border: "1px solid #f0f0f0",
            }}
          >
            No data
          </span>
        </div>
      ),
    },
    {
      title: "Last Activity",
      key: "lastActivity",
      width: 110,
      render: (_, r) => r.lastFeedbackDate ? (
        <span style={{ fontSize: 12, color: "#666" }}>
          {new Date(r.lastFeedbackDate).toLocaleDateString("en-IN")}
        </span>
      ) : (
        <span style={{ fontSize: 11, color: "#ccc", fontStyle: "italic" }}>No activity</span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 72,
      render: (_, r) => (
        <Space size={4} onClick={e => e.stopPropagation()}>
          <Tooltip title="View feedback history">
            <Button
              size="small"
              icon={<EyeOutlined />}
              style={{ color: "#014c75", borderColor: "#b3cfe0" }}
              onClick={() => openDrawer(r)}
            />
          </Tooltip>
          <Tooltip title="Submit feedback">
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              style={{ background: "#014c75", borderColor: "#014c75" }}
              onClick={() => { setSelected(r); setFeedbackModalOpen(true); }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#014c75", marginBottom: 4 }}>
          Talent Performance
        </div>
        <div style={{ fontSize: 13, color: "#999" }}>
          Track performance, raise concerns, and review feedback for all your hired talents.
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          {
            title: "Hired Talents",
            value: totalHired,
            icon: <TeamOutlined />,
            bg: "#f0f7ff",
            accent: "#014c75",
            iconBg: "#014c75",
          },
          {
            title: "Open Issues",
            value: totalOpenIssues,
            icon: <ExclamationCircleOutlined />,
            bg: totalOpenIssues > 0 ? "#fff2f0" : "#f6ffed",
            accent: totalOpenIssues > 0 ? "#ff4d4f" : "#52c41a",
            iconBg: totalOpenIssues > 0 ? "#ff4d4f" : "#52c41a",
          },
          {
            title: "Avg Performance",
            value: overallAvg !== null ? `${overallAvg}%` : "—",
            icon: <RiseOutlined />,
            bg: "#f9f0ff",
            accent: scoreColor(overallAvg),
            iconBg: "#7c3aed",
          },
          {
            title: "Active Last 30 Days",
            value: recentActivity,
            icon: <FireOutlined />,
            bg: "#fffbe6",
            accent: "#fa8c16",
            iconBg: "#fa8c16",
          },
        ].map((s, i) => (
          <Col xs={24} sm={12} md={6} key={i}>
            <Card className="stat-card" bodyStyle={{ padding: "16px 20px" }} style={{ background: s.bg }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: s.iconBg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, opacity: 0.9,
                }}>
                  <span style={{ color: "#fff", fontSize: 18 }}>{s.icon}</span>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: s.accent, lineHeight: 1.2, marginTop: 2 }}>
                    {s.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{
          display: "flex", gap: 10, padding: "16px 20px 0",
          borderBottom: "1px solid #f0f0f0", marginBottom: 0, flexWrap: "wrap",
          alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Input
              placeholder="Search by name, role, job or contract ID..."
              prefix={<SearchOutlined style={{ color: "#bbb" }} />}
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ width: 260, borderRadius: 8, fontSize: 13 }}
              allowClear
            />
            <Select
              placeholder={<span><FilterOutlined style={{ marginRight: 6 }} />Filter</span>}
              value={filterStatus}
              onChange={v => { setFilterStatus(v); setPage(1); }}
              style={{ width: 170, borderRadius: 8 }}
              allowClear
            >
              <Option value="active">Active Contracts</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="terminated">Terminated</Option>
              <Option value="with_issues">With Open Issues</Option>
              <Option value="no_feedback">No Feedback Yet</Option>
            </Select>
          </div>
          <div style={{ fontSize: 12, color: "#999", paddingBottom: 0 }}>
            {filtered.length} talent{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>

        <Table
          className="perf-table"
          columns={columns}
          dataSource={filtered}
          rowKey="hiringRecordId"
          loading={loading}
          style={{ padding: "0 4px" }}
          pagination={{
            current: page,
            pageSize,
            total: filtered.length,
            onChange: p => setPage(p),
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
            showSizeChanger: false,
            style: { padding: "12px 16px" },
          }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hired talents found" style={{ padding: "40px 0" }} /> }}
          onRow={record => ({
            onClick: () => openDrawer(record),
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        width={680}
        styles={{ header: { borderBottom: "1px solid #f0f0f0", paddingBottom: 16 } }}
        title={
          selected ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar
                size={44}
                icon={<UserOutlined />}
                style={{ background: "linear-gradient(135deg, #014c75, #0288c7)", flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#014c75", lineHeight: 1.3 }}>
                  {selected.talent?.name}
                </div>
                <div style={{ fontSize: 12, color: "#999" }}>
                  {selected.talent?.role}
                  {selected.talent?.experienceYears ? ` · ${selected.talent.experienceYears} yrs exp` : ""}
                </div>
              </div>
            </div>
          ) : "Talent Details"
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: "#014c75", borderColor: "#014c75", borderRadius: 8, fontWeight: 600 }}
            onClick={() => setFeedbackModalOpen(true)}
          >
            Give Feedback
          </Button>
        }
      >
        {selected && (
          <>
            <Row gutter={12} style={{ marginBottom: 20 }}>
              {[
                { label: "Total Feedback", value: selected.totalFeedback, color: "#014c75", bg: "#f0f7ff" },
                { label: "Open Issues", value: selected.openIssues, color: selected.openIssues > 0 ? "#ff4d4f" : "#52c41a", bg: selected.openIssues > 0 ? "#fff2f0" : "#f6ffed" },
                { label: "Avg Score", value: selected.avgScore !== null ? `${selected.avgScore}%` : "—", color: scoreColor(selected.avgScore), bg: "#f9f0ff" },
              ].map((s, i) => (
                <Col span={8} key={i}>
                  <div style={{
                    textAlign: "center", borderRadius: 10, padding: "14px 8px",
                    background: s.bg, border: "1px solid rgba(0,0,0,0.04)",
                  }}>
                    <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                      {s.label}
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  </div>
                </Col>
              ))}
            </Row>

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
              {(selected.talent?.primarySkills || []).slice(0, 3).map(s => (
                <Tag key={s} color="blue" style={{ borderRadius: 20, fontSize: 11 }}>{s}</Tag>
              ))}
              {selected.jobId && (
                <Tag icon={<FileTextOutlined />} color="blue" style={{ borderRadius: 20, fontSize: 11 }}>
                  {selected.jobId}
                </Tag>
              )}
              {selected.hiringRecordId && (
                <Tag color="geekblue" style={{ borderRadius: 20, fontSize: 11 }}>
                  {selected.hiringRecordId}
                </Tag>
              )}
              {selected.onboardingDate && (
                <Tag icon={<CalendarOutlined />} style={{ borderRadius: 20, fontSize: 11, color: "#555" }}>
                  Since {new Date(selected.onboardingDate).toLocaleDateString("en-IN")}
                </Tag>
              )}
            </div>

            <Divider
              orientation="left"
              orientationMargin={0}
              style={{ color: "#014c75", fontWeight: 700, fontSize: 13, margin: "0 0 16px" }}
            >
              Feedback History
              {feedbackHistory.length > 0 && (
                <span style={{
                  marginLeft: 8, fontSize: 11, background: "#014c75", color: "#fff",
                  borderRadius: 20, padding: "1px 7px", fontWeight: 600,
                }}>
                  {feedbackHistory.length}
                </span>
              )}
            </Divider>

            {feedbackLoading ? (
              <div style={{ textAlign: "center", padding: 48 }}><Spin /></div>
            ) : feedbackHistory.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#444", marginBottom: 6 }}>No feedback yet</div>
                <div style={{ fontSize: 12, color: "#aaa", marginBottom: 20 }}>
                  Share your experience working with this talent.
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ background: "#014c75", borderRadius: 8 }}
                  onClick={() => setFeedbackModalOpen(true)}
                >
                  Give First Feedback
                </Button>
              </div>
            ) : (
              <Timeline
                items={feedbackHistory.map(f => ({
                  color: feedbackTypeColor[f.feedbackType] || "blue",
                  children: (
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: 10,
                        border: "1px solid #e8eef5",
                        padding: "12px 16px",
                        marginBottom: 4,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 6 }}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                          <Tag
                            color={feedbackTypeColor[f.feedbackType]}
                            style={{ borderRadius: 20, fontWeight: 600 }}
                          >
                            {feedbackTypeLabel(f)}
                          </Tag>
                          {f.instantSubType && (
                            <Tag style={{ borderRadius: 20, fontSize: 11 }}>
                              {f.instantSubType.replace(/_/g, " ")}
                            </Tag>
                          )}
                          {f.severity && (
                            <Tag
                              color={f.severity === "high" ? "red" : f.severity === "medium" ? "orange" : "default"}
                              style={{ borderRadius: 20, fontSize: 11 }}
                            >
                              {f.severity}
                            </Tag>
                          )}
                          {statusTag(f.status)}
                        </div>
                        <span style={{ fontSize: 11, color: "#bbb", whiteSpace: "nowrap" }}>
                          {new Date(f.createdAt).toLocaleDateString("en-IN")}
                        </span>
                      </div>

                      {f.title && (
                        <div style={{ fontWeight: 600, fontSize: 13, marginTop: 8, color: "#222" }}>{f.title}</div>
                      )}
                      {(f.description || f.comments) && (
                        <div style={{ fontSize: 12, color: "#555", marginTop: 4, lineHeight: 1.7 }}>
                          {f.description || f.comments}
                        </div>
                      )}

                      {f.ratingBucket && (
                        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                          <Tag color="geekblue" style={{ borderRadius: 20, fontSize: 11 }}>
                            {f.ratingBucket.replace(/_/g, " ")}
                          </Tag>
                          {f.overallScore !== null && f.overallScore !== undefined && (
                            <span style={{ fontWeight: 700, color: scoreColor(f.overallScore), fontSize: 14 }}>
                              {f.overallScore}%
                            </span>
                          )}
                        </div>
                      )}

                      {(f.coreStrengths || f.areasOfImprovement || f.keyAchievements) && (
                        <div style={{
                          marginTop: 10, paddingTop: 10,
                          borderTop: "1px dashed #eee", fontSize: 12, color: "#666",
                        }}>
                          {f.coreStrengths && <div style={{ marginBottom: 2 }}><strong style={{ color: "#444" }}>Strengths:</strong> {f.coreStrengths}</div>}
                          {f.areasOfImprovement && <div style={{ marginBottom: 2 }}><strong style={{ color: "#444" }}>To Improve:</strong> {f.areasOfImprovement}</div>}
                          {f.keyAchievements && <div><strong style={{ color: "#444" }}>Achievements:</strong> {f.keyAchievements}</div>}
                        </div>
                      )}

                      {f.wouldRehire && (
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                          <strong style={{ color: "#666" }}>Would Rehire:</strong>{" "}
                          <Tag style={{ borderRadius: 20, fontSize: 11 }}>{f.wouldRehire}</Tag>
                        </div>
                      )}

                      {f.responses && f.responses.length > 0 && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f5f5f5" }}>
                          <div style={{ fontSize: 11, color: "#999", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                            <MessageOutlined style={{ fontSize: 11 }} />
                            <strong>{f.responses.length}</strong>{" "}response{f.responses.length > 1 ? "s" : ""}
                          </div>
                          {f.responses.map(r => {
                            const isCustomerReply = r.respondedByRole === "customer";
                            const isAdminReply = r.respondedByRole === "admin";
                            return (
                              <div
                                key={r.id}
                                style={{
                                  padding: "8px 12px",
                                  background: isCustomerReply ? "#f0fff4" : isAdminReply ? "#fffbe6" : "#f8fbfe",
                                  borderRadius: 8,
                                  marginBottom: 6,
                                  borderLeft: `3px solid ${isCustomerReply ? "#52c41a" : isAdminReply ? "#faad14" : "#91caff"}`,
                                }}
                              >
                                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 3 }}>
                                  <strong style={{ color: isCustomerReply ? "#389e0d" : isAdminReply ? "#d48806" : "#555" }}>
                                    {isCustomerReply ? "You" : isAdminReply ? "Admin" : "Partner"}
                                  </strong>
                                  {" · "}<Tag style={{ fontSize: 10, borderRadius: 20, padding: "0 6px" }}>{r.responseType?.replace(/_/g, " ")}</Tag>
                                  {" · "}{new Date(r.createdAt).toLocaleDateString("en-IN")}
                                </div>
                                <div style={{ fontSize: 12, color: "#444", lineHeight: 1.6 }}>{r.content}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" }}>
                        {f.status !== "closed" && (
                          <Button
                            size="small"
                            icon={<CheckCircleOutlined />}
                            loading={resolvingId === f.id}
                            onClick={() => handleResolve(f.id)}
                            style={{ fontSize: 11, color: "#389e0d", borderColor: "#b7eb8f", background: "#f6ffed" }}
                          >
                            Mark Resolved
                          </Button>
                        )}
                        <Button
                          size="small"
                          icon={<MessageOutlined />}
                          onClick={() => setReplyOpen(p => ({ ...p, [f.id]: !p[f.id] }))}
                          style={{ fontSize: 11, color: "#014c75", borderColor: "#b3cfe0", background: "#fff" }}
                        >
                          {replyOpen[f.id] ? "Cancel" : "Reply"}
                        </Button>
                      </div>

                      {replyOpen[f.id] && (
                        <div style={{ marginTop: 8 }}>
                          <TextArea
                            rows={2}
                            placeholder="Type your reply…"
                            value={replyText[f.id] || ""}
                            onChange={e => setReplyText(p => ({ ...p, [f.id]: e.target.value }))}
                            style={{ borderRadius: 8, fontSize: 12, resize: "none" }}
                          />
                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                            <Button
                              type="primary"
                              size="small"
                              icon={<SendOutlined />}
                              loading={submittingReply[f.id]}
                              disabled={!(replyText[f.id] || "").trim()}
                              onClick={() => handleReply(f.id)}
                              style={{ background: "#014c75", borderColor: "#014c75", fontSize: 11 }}
                            >
                              Send Reply
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
            )}
          </>
        )}
      </Drawer>

      <TalentFeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        onSuccess={() => {
          fetchOverview();
          if (selected) fetchFeedbackForRecord(selected.hiringRecordId);
        }}
        hiringRecordId={selected?.hiringRecordId || null}
        talentName={selected?.talent?.name || null}
      />
    </Wrapper>
  );
};

export default CustomerTalentPerformance;
